/**
 * Copyright 2025 Lumavec Group LLC
 *
 * All rights reserved. No part of this software may be used, copied, modified,
 * or distributed without explicit written permission. This software is provided
 * "as-is" without warranty of any kind, express or implied, including but not
 * limited to warranties of merchantability or fitness for a particular purpose.
 */

/**
 * Database schema and types for the System Agents feature
 * 
 * System agents are a special category of agents that run automated oversight
 * tasks across all products in a Velocity instance. They extend the existing
 * `agents` table by using specific categories and metadata.
 * 
 * @see product/docs/VELOCITY_SYSTEM_AGENTS.md for full design documentation
 */

import { pgTable, serial, text, jsonb, timestamp, boolean, integer, index } from 'drizzle-orm/pg-core';
import { createInsertSchema, createSelectSchema } from 'drizzle-zod';
import { z } from 'zod';
import { agents } from './agents';

// ============================================================================
// System Agent Categories
// ============================================================================

/**
 * System agent categories - used to identify agents as system agents
 * These are in addition to the regular agent categories in agents.ts
 */
export const SYSTEM_AGENT_CATEGORIES = [
  'system_audit',       // Security, vulnerability scanning
  'system_measurement', // Coverage, metrics
  'system_review',      // Architecture, code quality
  'system_evaluation',  // Documentation, dependencies
] as const;

export type SystemAgentCategory = typeof SYSTEM_AGENT_CATEGORIES[number];

/**
 * Check if a category is a system agent category
 */
export function isSystemAgentCategory(category: string): category is SystemAgentCategory {
  return SYSTEM_AGENT_CATEGORIES.includes(category as SystemAgentCategory);
}

// ============================================================================
// System Agent Run Status
// ============================================================================

export const SYSTEM_AGENT_RUN_STATUS = [
  'pending',
  'running',
  'completed',
  'failed',
  'cancelled',
] as const;

export type SystemAgentRunStatus = typeof SYSTEM_AGENT_RUN_STATUS[number];

// ============================================================================
// System Agent Run Result
// ============================================================================

export const SYSTEM_AGENT_RUN_RESULT = [
  'pass',
  'warning',
  'fail',
] as const;

export type SystemAgentRunResult = typeof SYSTEM_AGENT_RUN_RESULT[number];

// ============================================================================
// Finding Severity
// ============================================================================

export const FINDING_SEVERITY = [
  'critical',
  'high',
  'medium',
  'low',
  'info',
] as const;

export type FindingSeverity = typeof FINDING_SEVERITY[number];

// ============================================================================
// Finding Status
// ============================================================================

export const FINDING_STATUS = [
  'open',
  'acknowledged',
  'dismissed',
  'fixed',
] as const;

export type FindingStatus = typeof FINDING_STATUS[number];

// ============================================================================
// Trigger Source
// ============================================================================

export const TRIGGER_SOURCE = [
  'schedule',
  'manual',
  'mcp',
  'api',
] as const;

export type TriggerSource = typeof TRIGGER_SOURCE[number];

// ============================================================================
// System Agent Runs Table
// ============================================================================

/**
 * System Agent Runs - tracks multi-product agent runs
 * Links to agent_executions for individual per-product executions.
 */
export const systemAgentRuns = pgTable('system_agent_runs', {
  id: serial('id').primaryKey(),
  runId: text('run_id').notNull().unique(), // e.g., "RUN-20260111-001"
  agentId: integer('agent_id').notNull().references(() => agents.id),
  userId: integer('user_id'), // NULL for scheduled runs
  status: text('status').notNull().$type<SystemAgentRunStatus>().default('pending'),
  startedAt: timestamp('started_at'),
  completedAt: timestamp('completed_at'),
  durationSeconds: integer('duration_seconds'),
  targetProducts: jsonb('target_products'), // Array of product IDs
  productsAnalyzed: integer('products_analyzed').default(0),
  result: text('result').$type<SystemAgentRunResult>(), // 'pass', 'warning', 'fail'
  summary: text('summary'),
  errorMessage: text('error_message'),
  triggeredBy: text('triggered_by').notNull().$type<TriggerSource>().default('manual'),
  executionIds: jsonb('execution_ids'), // Array of agent_execution IDs for per-product runs
  createdAt: timestamp('created_at').defaultNow(),
  updatedAt: timestamp('updated_at').defaultNow(),
}, (table) => ({
  agentIdIdx: index('system_agent_runs_agent_idx').on(table.agentId),
  statusIdx: index('system_agent_runs_status_idx').on(table.status),
  triggeredByIdx: index('system_agent_runs_triggered_by_idx').on(table.triggeredBy),
  createdAtIdx: index('system_agent_runs_created_at_idx').on(table.createdAt),
  runIdIdx: index('system_agent_runs_run_id_idx').on(table.runId),
}));

// ============================================================================
// System Agent Schedules Table
// ============================================================================

/**
 * System Agent Schedules - manages scheduled execution of system agents
 */
export const systemAgentSchedules = pgTable('system_agent_schedules', {
  id: serial('id').primaryKey(),
  agentId: integer('agent_id').notNull().references(() => agents.id),
  cronExpression: text('cron_expression').notNull(),
  timezone: text('timezone').default('UTC'),
  targetProducts: text('target_products').default('all'), // 'all' | 'active' | JSON array of product IDs
  nextRunAt: timestamp('next_run_at'),
  lastRunAt: timestamp('last_run_at'),
  lastRunId: integer('last_run_id').references(() => systemAgentRuns.id),
  enabled: boolean('enabled').default(true),
  createdAt: timestamp('created_at').defaultNow(),
  updatedAt: timestamp('updated_at').defaultNow(),
}, (table) => ({
  agentIdIdx: index('system_agent_schedules_agent_idx').on(table.agentId),
  nextRunAtIdx: index('system_agent_schedules_next_run_idx').on(table.nextRunAt),
  enabledIdx: index('system_agent_schedules_enabled_idx').on(table.enabled),
}));

// ============================================================================
// System Agent Metadata Interface
// ============================================================================

/**
 * System agent metadata stored in Agent.metadata JSON field
 * This interface defines the additional configuration for system agents
 * beyond what's in the standard Agent schema.
 */
export interface SystemAgentMetadata {
  // Marker to identify system agents
  systemAgent: true;
  
  // Targeting configuration
  targetProducts: 'all' | 'active' | string[];
  
  // Execution settings
  timeoutSeconds: number;
  
  // Agent-specific configuration
  agentConfig: {
    // For security audit
    scanTypes?: ('secrets' | 'dependencies' | 'patterns' | 'auth')[];
    severityThreshold?: FindingSeverity;
    
    // For coverage analysis
    coverageThreshold?: number;
    includeIntegration?: boolean;
    
    // For documentation freshness
    maxStalenessDays?: number;
    includeApiDocs?: boolean;
    
    // For architecture review
    maxCyclomaticComplexity?: number;
    checkLayerViolations?: boolean;
    
    // Generic configuration
    [key: string]: unknown;
  };
  
  // Computed/cached values
  lastRunAt?: string;
  lastRunResult?: SystemAgentRunResult;
  lastRunId?: number;
}

/**
 * Helper to check if an agent is a system agent
 */
export function isSystemAgent(agent: { category: string; metadata?: unknown }): boolean {
  if (isSystemAgentCategory(agent.category)) {
    return true;
  }
  const metadata = agent.metadata as SystemAgentMetadata | null;
  return metadata?.systemAgent === true;
}

// ============================================================================
// Zod Schemas
// ============================================================================

// System Agent Runs
export const insertSystemAgentRunSchema = createInsertSchema(systemAgentRuns).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export const selectSystemAgentRunSchema = createSelectSchema(systemAgentRuns);

export const updateSystemAgentRunSchema = insertSystemAgentRunSchema.partial().omit({
  runId: true,
  agentId: true,
});

// System Agent Schedules
export const insertSystemAgentScheduleSchema = createInsertSchema(systemAgentSchedules).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export const selectSystemAgentScheduleSchema = createSelectSchema(systemAgentSchedules);

export const updateSystemAgentScheduleSchema = insertSystemAgentScheduleSchema.partial().omit({
  agentId: true,
});

// System Agent Metadata
export const systemAgentMetadataSchema = z.object({
  systemAgent: z.literal(true),
  targetProducts: z.union([
    z.literal('all'),
    z.literal('active'),
    z.array(z.string()),
  ]),
  timeoutSeconds: z.number().positive(),
  agentConfig: z.record(z.unknown()),
  lastRunAt: z.string().optional(),
  lastRunResult: z.enum(SYSTEM_AGENT_RUN_RESULT).optional(),
  lastRunId: z.number().optional(),
});

// ============================================================================
// Type Exports
// ============================================================================

export type SystemAgentRun = typeof systemAgentRuns.$inferSelect;
export type InsertSystemAgentRun = z.infer<typeof insertSystemAgentRunSchema>;
export type UpdateSystemAgentRun = z.infer<typeof updateSystemAgentRunSchema>;

/**
 * System Agent Finding - represents a finding discovered by a system agent
 * Note: Findings are stored in markdown files, not in the database
 */
export interface SystemAgentFinding {
  findingId: string; // e.g., "FND-20260111-001"
  runId: number;
  agentId: number;
  executionId?: string | null;
  productId?: string | null;
  severity: FindingSeverity;
  category: string;
  title: string;
  description?: string | null;
  filePath?: string | null;
  lineNumber?: number | null;
  codeSnippet?: string | null;
  recommendation?: string | null;
  status?: FindingStatus | null;
  dismissedReason?: string | null;
  fixedAt?: Date | null;
  fixedBy?: number | null;
  fixCommit?: string | null;
  createdAt: Date | null;
  updatedAt: Date | null;
}

/**
 * Insert data for creating a new finding
 */
export type InsertSystemAgentFinding = Omit<SystemAgentFinding, 'findingId' | 'createdAt' | 'updatedAt'>;

/**
 * Update data for modifying a finding
 */
export type UpdateSystemAgentFinding = Partial<Omit<SystemAgentFinding, 'id' | 'findingId' | 'runId' | 'agentId' | 'createdAt' | 'updatedAt'>>;

export type SystemAgentSchedule = typeof systemAgentSchedules.$inferSelect;
export type InsertSystemAgentSchedule = z.infer<typeof insertSystemAgentScheduleSchema>;
export type UpdateSystemAgentSchedule = z.infer<typeof updateSystemAgentScheduleSchema>;

// ============================================================================
// API Response Types
// ============================================================================

/**
 * Findings summary grouped by various dimensions
 */
export interface FindingsSummary {
  total: number;
  bySeverity: Record<FindingSeverity, number>;
  byCategory: Record<string, number>;
  byProduct: Record<string, number>;
  byAgent: Record<string, number>;
  byStatus: Record<FindingStatus, number>;
}

/**
 * System agent run with expanded agent info
 */
export interface SystemAgentRunWithAgent extends SystemAgentRun {
  agent?: {
    id: number;
    name: string;
    displayName: string;
    category: string;
  };
  findingsCount?: number;
}

/**
 * System agent schedule with expanded info
 */
export interface SystemAgentScheduleWithAgent extends SystemAgentSchedule {
  agent?: {
    id: number;
    name: string;
    displayName: string;
    category: string;
  };
}

/**
 * Options for executing a system agent
 */
export interface ExecuteSystemAgentOptions {
  userId?: number;
  targetProducts?: string[];
  triggeredBy: TriggerSource;
  agentConfig?: Record<string, unknown>;
}

/**
 * Options for listing system agent runs
 */
export interface ListSystemAgentRunsOptions {
  agentId?: number;
  status?: SystemAgentRunStatus;
  triggeredBy?: TriggerSource;
  limit?: number;
  offset?: number;
}

/**
 * Options for listing system agent findings
 */
export interface ListSystemAgentFindingsOptions {
  agentId?: number;
  runId?: number;
  productId?: string;
  severity?: FindingSeverity;
  status?: FindingStatus;
  category?: string;
  limit?: number;
  offset?: number;
}
